import { Info } from 'lucide-react';
import styles from '../BusinessProfileMain.module.css';
import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../utils/auth';
import Auth from '../../../utils/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Cropper from 'react-easy-crop';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { getCroppedImg } from '../../../utils/imageUtils';

const GeneralInformation = () => {
  const [fields, setFields] = useState({
    friendlyBusinessName: '',
    legalBusinessName: '',
    businessPhoneNumber: '',
    businessEmail: '',
    businessAddress: '',
    businessWebsite: '',
    businessNiche: '',
    address: '',
    city: '',
    postal: '',
    state: '',
    country: '',
    timezone: '',
    platformLanguage: '',
    outboundCommunicationLanguage: '',
    businessLogo: '',
    profileImage: { id: null, url: null, uploadedAt: null },
  });
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(false);
  const logoInputRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const workspaceId = Auth.getWorkspaceId();
  const userId = Auth.getUser()?.id;

  // GET on mount
  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/business-general-information/business-general-information`, {
          params: { workspaceId, userId }
        });
        if (data && !data.error) {
          setId(data.id || null);
          setFields({
            ...fields,
            ...data,
            profileImage: data.profileImage || { id: null, url: null, uploadedAt: null },
          });
          setLogoUrl(data.profileImage?.url || null);
        } else {
          setId(null);
        }
      } catch (err) {
        setId(null);
      } finally {
        setLoading(false);
      }
    };
    if (workspaceId && userId) fetchInfo();
    // eslint-disable-next-line
  }, [workspaceId, userId]);

  const handleLogoClick = () => {
    if (logoInputRef.current) logoInputRef.current.click();
  };



  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2.5 * 1024 * 1024) {
        toast.warn('File size exceeds 2.5 MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setOriginalImage(ev.target.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropAndUpload = async () => {
    if (!originalImage || !croppedAreaPixels) return;
    setLoading(true);
    try {
      const croppedBase64 = await getCroppedImg(originalImage, croppedAreaPixels, zoom);
      setLogoUrl(croppedBase64);
      setShowCropper(false);
      // Subir imagen recortada al backend
      const base64Data = croppedBase64.split(',')[1];
      const response = await axios.post(`${API_URL}/business-general-information/business-general-information-upload-image`, {
        businessGeneralInformationId: id,
        workspaceId,
        userId,
        fileName: 'business-logo.jpg',
        mimeType: 'image/jpeg',
        fileBuffer: base64Data
      });
      if (response.data && response.data.url) {
        setLogoUrl(response.data.url);
        setFields(prev => ({ ...prev, profileImage: { ...prev.profileImage, url: response.data.url } }));
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error('Unexpected server response when uploading image.');
      }
      logoInputRef.current.value = null;
    } catch (error) {
      toast.error('Error uploading image');
      logoInputRef.current.value = null;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setOriginalImage(null);
    if (logoInputRef.current) logoInputRef.current.value = null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        workspaceId,
        userId,
        friendlyBusinessName: fields.friendlyBusinessName,
        legalBusinessName: fields.legalBusinessName,
        businessPhoneNumber: fields.businessPhoneNumber,
        businessEmail: fields.businessEmail,
        businessAddress: fields.businessAddress,
        businessWebsite: fields.businessWebsite,
        businessNiche: fields.businessNiche,
        address: fields.address,
        city: fields.city,
        postal: fields.postal,
        state: fields.state,
        country: fields.country,
        timezone: fields.timezone,
        platformLanguage: fields.platformLanguage,
        outboundCommunicationLanguage: fields.outboundCommunicationLanguage,
      };

      if (id) {
        body.id = id;
      }

      const { data } = await axios.put(`${API_URL}/business-general-information/business-general-information`, body);
      if (data && !data.error) {
        toast.success('Information updated!');
        if (data.id) setId(data.id);
      } else {
        toast.error(data.error || 'Error updating information');
      }
    } catch (err) {
      console.error('Error updating information:', err);
      toast.error('Error updating information');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    if (!id || !workspaceId || !userId) {
      toast.error('Missing required fields');
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/delete-business-profile-image`, {
        params: {
          businessGeneralInformationId: id,
          workspaceId,
          userId
        }
      });
      if (response.data && response.data.message) {
        setLogoUrl(null);
        setFields(prev => ({ ...prev, profileImage: { ...prev.profileImage, url: null } }));
        toast.success('Profile image deleted successfully!');
      } else {
        toast.error('Error deleting profile image');
      }
    } catch (err) {
      toast.error('Error deleting profile image');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className={styles.profileSection}>
      {showCropper && (
        <div className={styles.cropperModal}>
          <div className={styles.cropperContent}>
            <div className={styles.cropperAreaWrapper}>
              <Cropper
                image={originalImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
                objectFit="horizontal-cover"
              />
            </div>
            <div className={styles.cropperZoomControl}>
              <label htmlFor="zoomRange">Zoom</label>
              <input
                id="zoomRange"
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={e => setZoom(Number(e.target.value))}
              />
            </div>
            <div className={styles.cropperActions}>
              <button className={styles.updateBtn} onClick={handleCropAndUpload} disabled={loading}>
                {loading ? 'Uploading...' : 'Crop and upload'}
              </button>
              <button className={styles.updateBtn} onClick={handleCancelCrop} disabled={loading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.businessProfileHeader}>General Information</h2>
          <div className={styles.locationId}>
            <span>Location ID</span>
            <Info className={styles.infoIcon} size={16} />
          </div>
        </div>
        <div className={styles.formGroup}>
          <div className={styles.profileImageContainer}>
            <div className={styles.profileImage}>
              {!showCropper && (
                logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Business Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                <div className={styles.defaultAvatar}>
                    <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                </div>
                )
              )}
              <div className={styles.cameraIcon} onClick={handleLogoClick} title="Change business logo">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={18} height={18}>
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  ref={logoInputRef}
                  style={{ display: 'none' }}
                  onChange={handleLogoChange}
                />
              </div>
            </div>
            <div className={styles.profileImageText}>
              <p>Business Logo</p>
              <p className={styles.imageSizeText}>The proposed size is 350×180 px no bigger than 2.5 MB</p>
              {logoUrl && (
                <>
                  <button type="button" className={styles.deleteProfileImageBtnSmall} onClick={() => setShowDeleteModal(true)} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                  <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
                    <DialogTitle>Delete Profile Image</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Are you sure you want to delete the business profile image? This action cannot be undone.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <button onClick={() => setShowDeleteModal(false)} className={styles.cancelProfileImageBtn}>Cancel</button>
                      <button onClick={handleDeleteProfileImage} className={styles.deleteProfileImageBtn} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>Friendly Business Name</label>
          <input name="friendlyBusinessName" value={fields.friendlyBusinessName} onChange={handleChange} type="text" placeholder="Enter business name" className={styles.formControl} />
        </div>
        <div className={styles.formGroup}>
          <label>Legal Business Name <Info className={styles.infoIcon} size={16} /></label>
          <input name="legalBusinessName" value={fields.legalBusinessName} onChange={handleChange} type="text" placeholder="Enter legal business name" className={styles.formControl} />
          <small className={styles.formText}>Enter the exact legal business name, as registered with the IRS.</small>
        </div>
        <div className={styles.formGroup}>
          <div className={styles.twoCol}>
            <div>
              <label>Business Email</label>
              <input name="businessEmail" value={fields.businessEmail} onChange={handleChange} type="email" placeholder="business@example.com" className={styles.formControl} />
            </div>
            <div>
              <label>Business Phone</label>
              <input name="businessPhoneNumber" value={fields.businessPhoneNumber} onChange={handleChange} type="tel" placeholder="(000) 000-0000" className={styles.formControl} />
            </div>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>Business Website</label>
          <input name="businessWebsite" value={fields.businessWebsite} onChange={handleChange} type="url" placeholder="https://www.example.com" className={styles.formControl} />
        </div>
        <div className={styles.formGroup}>
          <label>Business Niche</label>
          <input name="businessNiche" value={fields.businessNiche} onChange={handleChange} type="text" placeholder="Enter business niche" className={styles.formControl} />
        </div>
        {/* Agrega aquí los demás campos si los necesitas */}
        <div className={styles.updateSection}>
          <button className={styles.updateBtn} type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update Information'}</button>
        </div>
      </form>
    </div>
  );
};

export default GeneralInformation; 